import { z } from "zod";
import { STORED_DOMAIN_SIGN_IN_MODES } from "./auth.ts";
import { tenantIdSchema, tenantStatusSchema, uuidSchema } from "./tenancy.ts";

/**
 * The instance operator console (openspec change operator-console): plans and limits, instance
 * policies, tenant lifecycle and the operator view of a tenant, and support access.
 */

// ── Plans and limits (spec `tenant-plans`) ──────────────────────────────────────────────────

/** Upper bounds that keep a typo from looking like "unlimited". */
const MAX_SEATS = 10_000_000;
const MAX_STORAGE_BYTES = 1024 ** 5; // 1 PiB
const MAX_OCR_PAGES = 100_000_000;
const MAX_MONTHLY_USD = 100_000_000;

/** Each limit: a non-negative number, or null for unlimited. */
export const planLimitsSchema = z.strictObject({
  /** Active users; disabled users and support accounts do not count. */
  seats: z.int().min(0).max(MAX_SEATS).nullable(),
  /** Bytes of live documents. */
  storageBytes: z.int().min(0).max(MAX_STORAGE_BYTES).nullable(),
  /** OCR pages per calendar month (UTC). */
  ocrMonthlyPages: z.int().min(0).max(MAX_OCR_PAGES).nullable(),
  /** Estimated model spend per calendar month (UTC), in USD. */
  modelMonthlyUsd: z.number().min(0).max(MAX_MONTHLY_USD).nullable(),
});
export type PlanLimits = z.infer<typeof planLimitsSchema>;
export type PlanLimitKey = keyof PlanLimits;
export const PLAN_LIMIT_KEYS = [
  "seats",
  "storageBytes",
  "ocrMonthlyPages",
  "modelMonthlyUsd",
] as const;

/** `hard`: new conversation turns are refused once the spend limit is reached; `soft` warns. */
export const modelLimitModeSchema = z.enum(["soft", "hard"]);
export type ModelLimitMode = z.infer<typeof modelLimitModeSchema>;

export const planSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  limits: planLimitsSchema,
  modelLimitMode: modelLimitModeSchema,
  isDefault: z.boolean(),
  /** Tenants assigned to it (deleted tenants excluded). */
  tenantCount: z.int().min(0),
  updatedAt: z.iso.datetime(),
});
export type Plan = z.infer<typeof planSchema>;

export const planRequestSchema = z.strictObject({
  name: z.string().trim().min(1).max(80),
  limits: planLimitsSchema,
  modelLimitMode: modelLimitModeSchema.default("soft"),
  /** Makes it the plan new tenants get (the previous default stops being one). */
  isDefault: z.boolean().optional(),
});
export type PlanRequest = z.input<typeof planRequestSchema>;

/** A tenant's overrides: a limit present here replaces the plan's (null = follow the plan). */
export const limitOverridesSchema = z.strictObject({
  seats: z.int().min(0).max(MAX_SEATS).nullable(),
  storageBytes: z.int().min(0).max(MAX_STORAGE_BYTES).nullable(),
  ocrMonthlyPages: z.int().min(0).max(MAX_OCR_PAGES).nullable(),
  modelMonthlyUsd: z.number().min(0).max(MAX_MONTHLY_USD).nullable(),
});
export type LimitOverrides = z.infer<typeof limitOverridesSchema>;

/** `PUT /api/operator/tenants/:tenantId/limits`: the plan, and every override (null = none). */
export const tenantLimitsRequestSchema = z.strictObject({
  planId: uuidSchema,
  overrides: limitOverridesSchema,
});
export type TenantLimitsRequest = z.infer<typeof tenantLimitsRequestSchema>;

export const tenantLimitsSchema = z.object({
  plan: z.object({ id: uuidSchema, name: z.string(), modelLimitMode: modelLimitModeSchema }),
  overrides: limitOverridesSchema,
  /** The override when set, else the plan's limit. */
  effective: planLimitsSchema,
});
export type TenantLimits = z.infer<typeof tenantLimitsSchema>;

/** One limit as the tenant sees it: its effective value and the current use. */
export const limitUseSchema = z.object({
  limit: z.number().nullable(),
  used: z.number(),
  /** At least 80 % of a set limit. */
  warning: z.boolean(),
  /** At or above a set limit. */
  reached: z.boolean(),
});
export type LimitUse = z.infer<typeof limitUseSchema>;

/** `GET /api/admin/plan`: tenant admins, and entity admins read-only for the whole tenant. */
export const tenantPlanViewSchema = z.object({
  plan: z.object({ name: z.string(), modelLimitMode: modelLimitModeSchema }),
  /** Calendar month (UTC) of the monthly figures, `YYYY-MM`. */
  month: z.string(),
  seats: limitUseSchema,
  storageBytes: limitUseSchema,
  ocrPages: limitUseSchema,
  /**
   * `used` and `warning` are the displayed total: live plus synthetic demo spend
   * (demo-world-v2). `reached` is live spend only, as turns are refused on live spend only.
   */
  modelSpendUsd: limitUseSchema.extend({
    /** Calls of unknown cost this month: counted apart, never as free. */
    unknownCostCalls: z.int().min(0),
    /**
     * Synthetic demo spend included in `used` (dev-tier stages only; 0 elsewhere). Optional for
     * one release: an older API revision during a canary does not send it.
     */
    syntheticUsd: z.number().min(0).optional(),
  }),
});
export type TenantPlanView = z.infer<typeof tenantPlanViewSchema>;

// ── Tenants (spec `tenant-administration`) ──────────────────────────────────────────────────

/** The grace period between scheduling a deletion and the purge. */
export const TENANT_DELETION_GRACE_DAYS = 30;

/** A tenant in the operator's list: status, plan and this month's OCR use, no content. */
export const operatorTenantSchema = z.object({
  id: tenantIdSchema,
  name: z.string(),
  status: tenantStatusSchema,
  origin: z.enum(["self_signup", "managed"]),
  createdAt: z.iso.datetime(),
  deletionDueAt: z.iso.datetime().nullable(),
  plan: z.object({ id: uuidSchema, name: z.string() }),
  /** Active users, support accounts excluded (from the admin summary). */
  userCount: z.int().min(0),
  ocrBudget: z.object({
    /** The effective OCR limit (override, else the plan's); null = unlimited. */
    monthlyPages: z.int().min(0).nullable(),
    /** Pages reserved this calendar month (UTC). */
    usedThisMonth: z.int().min(0),
  }),
  /**
   * This month's estimated model spend against the effective limit (spec `tenant-plans`).
   * Optional for one release: an older API revision during a canary does not send it.
   */
  modelSpend: z
    .object({
      /** The effective limit in USD (override, else the plan's); null = unlimited. */
      monthlyUsd: z.number().min(0).nullable(),
      mode: modelLimitModeSchema,
      /**
       * Estimated spend this calendar month (UTC), in USD, rounded to cents: live plus synthetic
       * demo spend (demo-world-v2).
       */
      usedThisMonth: z.number().min(0),
      /**
       * `warning` from 80 % of the limit (displayed total), `reached` once live spend alone is at
       * the limit; null below or unlimited.
       */
      alert: z.enum(["warning", "reached"]).nullable(),
      /** The synthetic demo spend in `usedThisMonth`; absent when there is none. */
      syntheticUsd: z.number().min(0).optional(),
    })
    .optional(),
});
export type OperatorTenant = z.infer<typeof operatorTenantSchema>;

/**
 * Why an invitation e-mail did not go out (tenant-admin-provisioning D5): the recipient is not
 * verified while the e-mail service is in its sandbox, the deployment may not send, sending is
 * paused for the account, or anything else.
 */
export const INVITATION_FAILURE_REASONS = [
  "sandbox_unverified",
  "not_authorized",
  "sending_paused",
  "unknown",
] as const;
export const invitationFailureReasonSchema = z.enum(INVITATION_FAILURE_REASONS);
export type InvitationFailureReason = z.infer<typeof invitationFailureReasonSchema>;

/** Whether an invitation e-mail went out, for the operator or admin who sent it. */
export const invitationOutcomeSchema = z.object({
  sent: z.boolean(),
  reason: invitationFailureReasonSchema.optional(),
});
export type InvitationOutcome = z.infer<typeof invitationOutcomeSchema>;

/**
 * A tenant admin's latest invitation as operators see it: `pending` (sent at `at`, or not
 * recorded), `failed` with its reason, or `expired`. Null once accepted or never invited.
 */
export const adminInvitationStateSchema = z.object({
  state: z.enum(["pending", "failed", "expired"]),
  reason: invitationFailureReasonSchema.nullable(),
  at: z.string().nullable(),
});
export type AdminInvitationState = z.infer<typeof adminInvitationStateSchema>;

/**
 * The operator view of one tenant (spec "Operator view of a tenant's administration"): its
 * tenant admins' names and e-mails, identity providers, verified domains and counts. Never an
 * entity or project name, nor any other user.
 */
export const operatorTenantDetailSchema = operatorTenantSchema.extend({
  admins: z.array(
    z.object({
      /** Absent for a summary written before tenant-admin-provisioning (no Resend then). */
      id: z.string().optional(),
      name: z.string().nullable(),
      email: z.string(),
      invitation: adminInvitationStateSchema.nullable().optional(),
    }),
  ),
  identityProviders: z.array(
    z.object({
      protocol: z.enum(["oidc", "saml"]),
      displayName: z.string(),
      status: z.enum(["active", "disabled"]),
    }),
  ),
  domains: z.array(z.string()),
  /**
   * Verified domains bound to a live IdP (#419, D13, read-only): the IdP's display name and the
   * mode (`effectiveSignInMode` equals it since #438, kept for the previous console).
   */
  domainSignIn: z
    .array(
      z.object({
        domain: z.string(),
        identityProvider: z.string(),
        // As stored: a previous API may still send the retired `sso_only` (#438); read it
        // with `readDomainSignInMode`.
        signInMode: z.enum(STORED_DOMAIN_SIGN_IN_MODES),
        effectiveSignInMode: z.enum(STORED_DOMAIN_SIGN_IN_MODES),
      }),
    )
    .optional(),
  entityCount: z.int().min(0),
  limits: tenantLimitsSchema,
  /**
   * Whether its scheduled deletion is due (expedited, or the grace period over) on the database's
   * clock, the one cancel and expedite check (#404): the purge has started. Optional for one
   * release: an older API revision during a canary does not send it.
   */
  deletionDue: z.boolean().optional(),
});
export type OperatorTenantDetail = z.infer<typeof operatorTenantDetailSchema>;

/** `POST /api/operator/tenants/:tenantId/deletion`: the tenant's exact name. */
export const scheduleDeletionRequestSchema = z.strictObject({
  confirmName: z.string().max(200),
});
export type ScheduleDeletionRequest = z.infer<typeof scheduleDeletionRequestSchema>;

/**
 * `POST /api/operator/tenants/:tenantId/deletion/expedite`: the tenant's exact name and the first
 * 8 characters of its ID (names are not unique).
 */
export const expediteDeletionRequestSchema = z.strictObject({
  confirmName: z.string().max(200),
  confirmId: z.string().max(36),
});
export type ExpediteDeletionRequest = z.infer<typeof expediteDeletionRequestSchema>;

/** How many characters of the tenant ID an expedite confirmation asks for (names repeat). */
export const TENANT_ID_PREFIX_LENGTH = 8;

/** The part of a tenant ID an operator types to confirm an expedite, lower-cased. */
export function tenantIdPrefix(tenantId: string): string {
  return tenantId.slice(0, TENANT_ID_PREFIX_LENGTH).toLowerCase();
}

/** `POST /api/admin/users/:userId/mfa-reset`: why the user's second factors are removed. */
export const mfaResetRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(500),
});
export type MfaResetRequest = z.infer<typeof mfaResetRequestSchema>;

/** `POST /api/operator/tenants/:tenantId/admin-mfa-reset`: a tenant admin, by e-mail. */
export const operatorMfaResetRequestSchema = mfaResetRequestSchema.extend({
  email: z.email(),
});
export type OperatorMfaResetRequest = z.infer<typeof operatorMfaResetRequestSchema>;

// ── Instance policies ───────────────────────────────────────────────────────────────────────

/** One setting as operators set it for the whole instance. */
export const instancePolicySchema = z.object({
  key: z.string(),
  area: z.string(),
  description: z.string(),
  /** How the value is edited (as the backoffice settings catalogue). */
  input: z.unknown(),
  /** The code default. */
  defaultValue: z.unknown(),
  /** The instance value, when one is stored. */
  value: z.unknown().optional(),
  /** What every tenant inherits (the deployment lock, the instance value or the default). */
  effectiveValue: z.unknown(),
  locked: z.boolean(),
  /** Set when the deployment fixes the value: it cannot be changed here. */
  deploymentLock: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type InstancePolicy = z.infer<typeof instancePolicySchema>;

export const putInstancePolicyRequestSchema = z.strictObject({
  value: z.unknown(),
  locked: z.boolean(),
});
export type PutInstancePolicyRequest = z.infer<typeof putInstancePolicyRequestSchema>;

// ── Support access (spec `support-access`) ──────────────────────────────────────────────────

export const SUPPORT_MAX_HOURS = 72;
/** An unanswered request expires after this many days. */
export const SUPPORT_REQUEST_TTL_DAYS = 7;

export const supportGrantStatusSchema = z.enum([
  "requested",
  "active",
  "refused",
  "revoked",
  "expired",
]);
export type SupportGrantStatus = z.infer<typeof supportGrantStatusSchema>;

export const supportGrantSchema = z.object({
  id: uuidSchema,
  tenantId: tenantIdSchema,
  operatorId: z.string(),
  reason: z.string(),
  requestedHours: z.int(),
  approvedHours: z.int().nullable(),
  status: supportGrantStatusSchema,
  requestedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime().nullable(),
  decidedAt: z.iso.datetime().nullable(),
  endedAt: z.iso.datetime().nullable(),
  /** `content` when the tenant opened projects to the grant. */
  scope: z.enum(["metadata", "content"]),
  contentProjectIds: z.array(uuidSchema),
});
export type SupportGrant = z.infer<typeof supportGrantSchema>;

/** A grant as its operator lists it, with the tenant's name. */
export const operatorSupportGrantSchema = supportGrantSchema.extend({ tenantName: z.string() });
export type OperatorSupportGrant = z.infer<typeof operatorSupportGrantSchema>;

/** A grant as the tenant's admins see it: the projects opened by name, and its use. */
export const adminSupportGrantSchema = supportGrantSchema.extend({
  contentProjects: z.array(z.object({ id: uuidSchema, name: z.string() })),
  /** Requests the operator made under it. */
  uses: z.int().min(0),
  lastUsedAt: z.iso.datetime().nullable(),
});
export type AdminSupportGrant = z.infer<typeof adminSupportGrantSchema>;

export const supportRequestSchema = z.strictObject({
  tenantId: uuidSchema,
  reason: z.string().trim().min(1).max(500),
  hours: z.int().min(1).max(SUPPORT_MAX_HOURS),
});
export type SupportRequest = z.infer<typeof supportRequestSchema>;

/** Approve as asked, or for less time (`hours` at most the request's). */
export const approveSupportRequestSchema = z.strictObject({
  hours: z.int().min(1).max(SUPPORT_MAX_HOURS).optional(),
});
export type ApproveSupportRequest = z.infer<typeof approveSupportRequestSchema>;

/** Content access: the projects the operator may read, replacing any earlier list. */
export const supportContentRequestSchema = z.strictObject({
  projectIds: z.array(uuidSchema).max(50),
});
export type SupportContentRequest = z.infer<typeof supportContentRequestSchema>;
